import * as S from "effect-app/Schema"
import { AiChatInput } from "../AiChat.ts"
import { LabelField, LabelTargetKind } from "./LabelUpdate.ts"

export const MAX_LABEL_AI_ROWS = 800
// bound on a single label text, both incoming (row texts) and outgoing (patch
// entries): keeps the prompt and the response bounded alongside the row cap
export const MAX_LABEL_AI_TEXT = 2000

export class LabelAiRow extends S.Opaque<LabelAiRow, LabelAiRow.Encoded>()(
  S
    .Struct({
      index: S.Int,
      kind: LabelTargetKind,
      field: LabelField,
      // the human-readable context path shown in the table
      context: S.NonEmptyString2k,
      // the row's current DRAFT text, "" when unset
      text: S.String.pipe(S.check(S.isMaxLength(MAX_LABEL_AI_TEXT)))
    })
    .annotate({ identifier: "LabelAiRow" })
) {}

/*
 * What the model is allowed to say. Each entry names a row by its `index`
 * from the table the client sent and carries the FULL replacement text ("" to
 * clear an optional field). The client shows the entries as a draft and the
 * user applies them. The model never invents a row.
 */
export class LabelPatch extends S.Opaque<LabelPatch, LabelPatch.Encoded>()(
  S
    .Struct({
      entries: S
        .Array(S.Struct({
          row: S.Int,
          text: S.String.pipe(S.check(S.isMaxLength(MAX_LABEL_AI_TEXT)))
        }))
        .pipe(S.check(S.isBetweenLength(0, MAX_LABEL_AI_ROWS))),
      reply: S.NonEmptyString2k
    })
    .annotate({ identifier: "LabelPatch" })
) {}

// What one interpretation turn needs, caps included. The single definition of
// the (message, history, rows) shape: the resource spreads these fields into
// its request (plus configuratorId), the service takes exactly this as input.
// A plain Struct on purpose: the type stays structural, so the controller can
// hand the decoded request fields straight through.
export const LabelAiInput = S.Struct({
  ...AiChatInput.fields,
  rows: S.Array(LabelAiRow).pipe(S.check(S.isBetweenLength(1, MAX_LABEL_AI_ROWS)))
})
export type LabelAiInput = typeof LabelAiInput.Type

// codegen:start {preset: model}
//
export namespace LabelAiRow {
  export interface Encoded extends S.StructNestedEncoded<typeof LabelAiRow> {}
}
export namespace LabelPatch {
  export interface Encoded extends S.StructNestedEncoded<typeof LabelPatch> {}
}
//
// codegen:end
